import { Component } from '@theme/component';
import { fetchConfig } from '@theme/utilities';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * Compre Junto — adiciona apenas os produtos marcados e suas variantes escolhidas.
 *
 * O desconto do "look" é só exibido no tema (calculado em Liquid). O desconto real
 * na sacola precisa de um desconto automático configurado no Shopify.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [total]
 * @property {HTMLButtonElement} botao
 * @extends {Component<Refs>}
 */
class CompreJunto extends Component {
  requiredRefs = ['botao'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('change', this.#onChange);
    this.refs.botao?.addEventListener('click', this.#onSubmit);
    this.#onChange();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('change', this.#onChange);
    this.refs.botao?.removeEventListener('click', this.#onSubmit);
  }

  #ids() {
    return Array.from(this.querySelectorAll('[data-item]'))
      .filter((item) => item.querySelector('.cj-look__checkbox')?.checked)
      .map((item) => item.querySelector('.cj-look__variant-input:checked')?.value)
      .filter(Boolean);
  }

  #total() {
    return Array.from(this.querySelectorAll('[data-item]'))
      .filter((item) => item.querySelector('.cj-look__checkbox')?.checked)
      .reduce((total, item) => total + Number(item.querySelector('.cj-look__variant-input:checked')?.dataset.price || 0), 0);
  }

  #onChange = () => {
    const pronto = this.#ids().length > 0;
    if (this.refs.botao) this.refs.botao.disabled = !pronto;
    if (this.refs.total) {
      this.refs.total.textContent = new Intl.NumberFormat(document.documentElement.lang || 'pt-BR', {
        style: 'currency', currency: this.dataset.currency || 'BRL',
      }).format(this.#total() / 100);
    }
  };

  /** @param {MouseEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();

    const ids = this.#ids();
    if (!ids.length) return;

    const botao = this.refs.botao;
    botao.disabled = true;
    botao.setAttribute('aria-busy', 'true');

    const formData = new FormData();
    ids.forEach((id, index) => {
      formData.append(`items[${index}][id]`, id);
      formData.append(`items[${index}][quantity]`, '1');
    });

    // Seções de carrinho pra atualizar (mesmo padrão do product-form.js).
    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((element) => /** @type {HTMLElement} */ (element).dataset.sectionId)
      .filter(Boolean);
    if (sectionIds.length) formData.append('sections', sectionIds.join(','));

    const irParaCarrinho = this.dataset.aposAdicionar === 'carrinho';

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: ids.map((id) => ({ merchandiseId: id, quantity: 1 })),
        promise: deferred.promise,
      })
    );

    const config = fetchConfig('javascript', { body: formData });

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        ...config,
        headers: { ...config.headers, Accept: 'text/html' },
      });
      const data = await response.json();

      if (data.status) {
        // Erro do Shopify (ex.: sem estoque) — reabilita e não navega.
        deferred.reject(new Error(data.message || 'Add to cart failed'));
        botao.disabled = false;
        botao.removeAttribute('aria-busy');
        return;
      }

      if (irParaCarrinho) {
        deferred.resolve({ detail: { didError: false } });
        window.location.href = Theme.routes.cart_url;
        return;
      }

      // Atualiza/abre a sacola (drawer) reusando o fluxo de eventos do tema.
      const ajaxCart = await fetch(`${Theme.routes.cart_url}.json`, {
        headers: { Accept: 'application/json' },
      }).then((res) => res.json());

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          sections: data.sections,
          source: 'compre-junto',
          didError: false,
        },
      });

      botao.disabled = false;
      botao.removeAttribute('aria-busy');
    } catch (error) {
      // Falha de rede: não perde o add — manda pro carrinho.
      deferred.reject(error);
      window.location.href = Theme.routes.cart_url;
    }
  };
}

if (!customElements.get('compre-junto')) {
  customElements.define('compre-junto', CompreJunto);
}
